<template>
  <div>
    <v-card outlined>
      <v-card-title>
        Pesistulokset events
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="fetchEvents">
          <v-text-field
            v-model.trim="gameUrl"
            label="Pesistulokset game URL"
            placeholder="https://www.pesistulokset.fi/ottelut/130001"
            :error-messages="urlError"
            :disabled="loading"
            outlined
            dense
          />
          <div class="d-flex align-center">
            <v-btn
              color="primary"
              type="submit"
              :loading="loading"
              :disabled="loading"
            >
              Fetch events
            </v-btn>
            <v-btn
              v-if="currentGameUrl"
              type="button"
              text
              color="primary"
              class="ml-2"
              :disabled="loading"
              @click="useCurrentGame"
            >
              Use current game
            </v-btn>
            <div v-if="gameId" class="ml-3 grey--text text--darken-1">
              Game id: {{ gameId }}
            </div>
          </div>
        </v-form>

        <v-alert v-if="fetchError" type="error" dense text class="mt-4 mb-0">
          {{ fetchError }}
        </v-alert>
      </v-card-text>
    </v-card>

    <div v-if="rawEvents" class="mt-4">
      <v-tabs v-model="tab" grow dense>
        <v-tab>Events</v-tab>
        <v-tab>Calibration</v-tab>
        <v-tab>Raw</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item>
          <v-card outlined tile>
            <v-card-title class="py-3">
              <div>
                <div>Events</div>
                <div class="text-caption grey--text text--darken-1">
                  {{ events.length }} tapahtumaa
                </div>
              </div>
              <v-spacer />
              <v-text-field
                v-model.trim="eventFilter"
                append-icon="mdi-magnify"
                label="Filter"
                single-line
                hide-details
                dense
                clearable
                class="event-filter"
              />
            </v-card-title>
            <v-card-text class="event-list">
              <v-alert v-if="!events.length" type="info" text dense>
                Response did not contain <code>data.events</code>.
              </v-alert>

              <v-card
                v-for="event in filteredEvents"
                :key="event.id"
                outlined
                class="mb-2 event-card"
                :class="{ 'selected-event': selectedEvent && selectedEvent.id === event.id }"
                @click="selectedEventId = event.id"
              >
                <v-card-text class="py-3">
                  <div class="d-flex align-start">
                    <div class="flex-grow-1">
                      <div class="d-flex align-center flex-wrap">
                        <v-chip x-small label class="mr-2 mb-1">
                          {{ event.groupType || '-' }}
                        </v-chip>
                        <v-chip x-small label outlined class="mr-2 mb-1">
                          id {{ event.id }}
                        </v-chip>
                        <v-chip x-small label outlined class="mr-2 mb-1">
                          {{ gamePhaseLabel(event) }}
                        </v-chip>
                        <v-chip
                          v-if="event.hitNumber"
                          x-small
                          label
                          outlined
                          class="mr-2 mb-1"
                        >
                          {{ event.hitNumber }}. lyönti
                        </v-chip>
                      </div>
                      <div class="font-weight-medium event-summary">
                        {{ eventSummary(event) }}
                      </div>
                      <div class="mt-1 text-caption grey--text text--darken-1">
                        <span v-if="event.team">Team {{ event.team }}</span>
                        <span v-if="event.batter"> · Batter #{{ event.batter }}</span>
                        <span v-if="event.hit"> · Hit {{ hitLocationLabel(event.hit) }}</span>
                      </div>
                      <div class="text-caption grey--text text--darken-1">
                        Event time {{ formatEventTimestamp(event.timestamp) }}
                        <span v-if="calibratedVideoTime(event) !== null">
                          · video {{ formatSeconds(calibratedVideoTime(event)) }}
                        </span>
                      </div>
                      <div v-if="eventRows(event).length > 1" class="mt-2 event-rows">
                        <div
                          v-for="(row, rowIndex) in eventRows(event)"
                          :key="event.id + '-row-' + rowIndex"
                          class="text-caption"
                        >
                          {{ rowIndex + 1 }}. {{ row.text }}
                        </div>
                      </div>
                      <div v-if="baseStateLabel(event)" class="mt-2">
                        <v-chip
                          v-for="(runner, index) in baseStateLabel(event)"
                          :key="event.id + '-' + index"
                          x-small
                          label
                          class="mr-1"
                          :outlined="!runner"
                          :color="runner ? 'primary' : undefined"
                        >
                          {{ baseLabel(index) }}: {{ runner || '-' }}
                        </v-chip>
                      </div>
                    </div>
                    <div class="d-flex flex-column ml-3">
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        :disabled="calibratedVideoTime(event) === null"
                        @click.stop="seekToEvent(event)"
                      >
                        Seek
                      </v-btn>
                      <v-btn
                        x-small
                        text
                        color="primary"
                        class="mt-1"
                        :disabled="event.timestamp === null || event.timestamp === undefined"
                        @click.stop="startCalibration(event)"
                      >
                        Calibrate
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined tile>
            <v-card-title class="py-3">
              Video calibration
            </v-card-title>
            <v-card-text>
              <v-alert type="info" text dense>
                Pick an event, seek the video to the exact same moment, then save an anchor. One anchor gives a fixed offset; two or more anchors interpolate between known points. Anchors are saved locally for this browser and game id.
              </v-alert>

              <div class="d-flex align-center flex-wrap mb-3">
                <div class="mr-4">
                  <div class="text-caption grey--text">Selected event</div>
                  <div class="font-weight-medium">
                    {{ selectedEvent ? eventSummary(selectedEvent) : 'Select event from Events tab' }}
                  </div>
                  <div v-if="selectedEvent" class="text-caption grey--text text--darken-1">
                    {{ formatEventTimestamp(selectedEvent.timestamp) }}
                  </div>
                </div>
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="mr-2"
                  :disabled="!selectedEvent || calibratedVideoTime(selectedEvent) === null"
                  @click="seekToEvent(selectedEvent)"
                >
                  Seek estimate
                </v-btn>
                <v-spacer />
                <div class="text-right mr-3">
                  <div class="text-caption grey--text">Current video time</div>
                  <div class="font-weight-medium">{{ formatSeconds(videoCurrentTime) }}</div>
                </div>
                <v-btn
                  color="primary"
                  :disabled="!canCalibrateSelectedEvent"
                  @click="saveCalibrationPoint"
                >
                  Save anchor
                </v-btn>
              </div>

              <v-card outlined class="mb-4 pa-3">
                <div class="font-weight-bold mb-1">Calibration model</div>
                <div class="text-body-2">
                  <span v-if="calibrationPoints.length === 0">
                    No anchors yet. Seek buttons are disabled until one event is aligned to video.
                  </span>
                  <span v-else-if="calibrationPoints.length === 1">
                    Using one offset: event timestamp + {{ formatSignedSeconds(singleCalibrationOffset) }}.
                  </span>
                  <span v-else>
                    Using interpolation between neighboring anchors, with nearest-edge extrapolation outside the anchored range.
                  </span>
                </div>
              </v-card>

              <v-simple-table dense>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Event time</th>
                    <th>Video time</th>
                    <th>Offset</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!calibrationPoints.length">
                    <td colspan="5" class="grey--text py-4">
                      No calibration anchors saved.
                    </td>
                  </tr>
                  <tr v-for="point in calibrationPointsSorted" :key="point.id">
                    <td>{{ point.eventLabel }}</td>
                    <td>{{ formatEventTimestamp(point.eventTimestamp) }}</td>
                    <td>{{ formatSeconds(point.videoTime) }}</td>
                    <td>{{ formatSignedSeconds(point.videoTime - point.eventTimestamp) }}</td>
                    <td class="text-right">
                      <v-btn x-small text color="primary" @click="seek(point.videoTime)">
                        Seek
                      </v-btn>
                      <v-btn x-small text color="error" @click="removeCalibrationPoint(point.id)">
                        Remove
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card outlined tile>
            <v-card-title>
              Raw response
            </v-card-title>
            <v-card-text>
              <pre class="raw-events">{{ formattedEvents }}</pre>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapGetters } from 'vuex'

export default {
  created() {
    this.useCurrentGame()
  },
  data: () => ({
    tab: 0,
    gameUrl: '',
    gameId: '',
    rawEvents: null,
    events: [],
    selectedEventId: null,
    eventFilter: '',
    calibrationPoints: [],
    loading: false,
    urlError: '',
    fetchError: ''
  }),
  computed: {
    ...mapGetters('videos', [
      'video'
    ]),
    ...mapGetters('player', [
      'accurateCurrentTime',
      'currentTime'
    ]),
    formattedEvents() {
      return JSON.stringify(this.rawEvents, null, 2)
    },
    filteredEvents() {
      const filter = (this.eventFilter || '').toLowerCase()
      if(!filter) return this.events

      return this.events.filter(event => {
        return [
          event.id,
          event.groupType,
          event.batter,
          event.team,
          event.timestamp,
          this.eventSummary(event)
        ].join(' ').toLowerCase().includes(filter)
      })
    },
    selectedEvent() {
      if(!this.selectedEventId) return this.events[0] || null

      return this.events.find(event => event.id === this.selectedEventId) || null
    },
    calibrationPointsSorted() {
      return [...this.calibrationPoints].sort((a, b) => a.eventTimestamp - b.eventTimestamp)
    },
    singleCalibrationOffset() {
      if(!this.calibrationPoints.length) return null

      const point = this.calibrationPointsSorted[0]
      return point.videoTime - point.eventTimestamp
    },
    canCalibrateSelectedEvent() {
      return this.selectedEvent && this.selectedEvent.timestamp !== null && this.selectedEvent.timestamp !== undefined
    },
    videoCurrentTime() {
      if(this.accurateCurrentTime !== null && this.accurateCurrentTime !== undefined) return this.accurateCurrentTime
      if(this.currentTime !== null && this.currentTime !== undefined) return this.currentTime

      return 0
    },
    currentGameUrl() {
      if(!this.video?.external_service_id) return ''

      return `https://www.pesistulokset.fi/ottelut/${this.video.external_service_id}`
    },
    calibrationStorageKey() {
      return `pesistulokset-event-calibration:${this.gameId || 'unknown'}`
    }
  },
  methods: {
    ...mapActions('player', [
      'seek'
    ]),
    extractGameId(url) {
      const trimmedUrl = url.trim()
      const withoutQuery = trimmedUrl.split(/[?#]/)[0]
      const parts = withoutQuery.split('/').filter(Boolean)

      return parts[parts.length - 1] || ''
    },
    validateGameId(gameId) {
      return /^\d+$/.test(gameId)
    },
    normalizeEvents(data) {
      if(Array.isArray(data?.data?.events)) return data.data.events
      if(Array.isArray(data?.events)) return data.events
      if(Array.isArray(data)) return data

      return []
    },
    useCurrentGame() {
      if(!this.currentGameUrl) return

      this.gameUrl = this.currentGameUrl
      this.gameId = this.extractGameId(this.gameUrl)
    },
    async fetchEvents() {
      this.urlError = ''
      this.fetchError = ''
      this.rawEvents = null
      this.events = []
      this.selectedEventId = null

      const gameId = this.extractGameId(this.gameUrl)
      this.gameId = gameId

      if(!this.validateGameId(gameId)) {
        this.urlError = 'Anna Pesistulokset-ottelun URL, joka päättyy numeeriseen ottelu-id:hen.'
        return
      }

      this.loading = true

      try {
        const { data } = await axios.get(`https://api.pesistulokset.fi/api/v1/online/${gameId}/events`)
        this.rawEvents = data
        this.events = this.normalizeEvents(data)
        this.selectedEventId = this.events[0]?.id || null
        this.loadCalibrationPoints()
      } catch(e) {
        this.fetchError = e?.response?.data?.message || e?.message || 'Events fetch failed'
      } finally {
        this.loading = false
      }
    },
    eventSummary(event) {
      return this.eventRows(event)
        .map(item => item.text)
        .filter(Boolean)
        .join(' · ') || '(no text)'
    },
    eventRows(event) {
      return (event?.events || [])
        .map(item => ({
          text: this.textsToString(item.texts),
          runnersAtBases: item.runnersAtBases
        }))
        .filter(item => item.text)
    },
    textsToString(texts) {
      if(!Array.isArray(texts)) return ''

      return texts.map(text => this.textPartToString(text)).filter(Boolean).join(' ')
    },
    textPartToString(part) {
      if(typeof part === 'string') return part
      if(!part || typeof part !== 'object') return ''
      if(part.type === 'event') return part.text
      if(part.type === 'player') return `#${part.id}`
      if(part.type === 'team') return `team ${part.id}`
      if(part.type === 'hit') return 'hit'
      if(part.type === 'stat') return ''

      return part.text || part.id || ''
    },
    hitLocationLabel(hit) {
      if(!hit?.x || !hit?.y) return ''

      return `x ${hit.x}, y ${hit.y}`
    },
    gamePhaseLabel(event) {
      const period = Number(event.period) + 1
      const inning = Number(event.inning) + 1
      const batTurn = Number(event.batTurn) + 1

      return `J${period} VP${inning} ${batTurn}. vuoro`
    },
    baseStateLabel(event) {
      const lastEventWithBases = [...(event?.events || [])].reverse().find(item => Array.isArray(item.runnersAtBases))
      if(!lastEventWithBases) return null

      return lastEventWithBases.runnersAtBases
    },
    baseLabel(index) {
      return ['K', '1', '2', '3', 'H'][index] || index
    },
    formatEventTimestamp(timestamp) {
      if(timestamp === null || timestamp === undefined) return '-'

      return `${timestamp}s`
    },
    formatSeconds(seconds) {
      if(seconds === null || seconds === undefined || Number.isNaN(seconds)) return '-'

      const safeSeconds = Math.max(0, seconds)
      const minutes = Math.floor(safeSeconds / 60)
      const rest = Math.floor(safeSeconds % 60).toString().padStart(2, '0')
      const decimals = (safeSeconds % 1).toFixed(1).slice(1)

      return `${minutes}:${rest}${decimals}`
    },
    formatSignedSeconds(seconds) {
      if(seconds === null || seconds === undefined || Number.isNaN(seconds)) return '-'

      const sign = seconds >= 0 ? '+' : '-'
      return `${sign}${Math.abs(seconds).toFixed(1)}s`
    },
    startCalibration(event) {
      this.selectedEventId = event.id
      this.tab = 1
    },
    saveCalibrationPoint() {
      if(!this.canCalibrateSelectedEvent) return

      const nextPoint = {
        id: `${this.selectedEvent.id}:${Date.now()}`,
        eventId: this.selectedEvent.id,
        eventTimestamp: this.selectedEvent.timestamp,
        eventLabel: this.eventSummary(this.selectedEvent),
        videoTime: this.videoCurrentTime,
        createdAt: new Date().toISOString()
      }

      this.calibrationPoints = this.calibrationPoints
        .filter(point => point.eventId !== nextPoint.eventId)
        .concat(nextPoint)
      this.persistCalibrationPoints()
    },
    removeCalibrationPoint(pointId) {
      this.calibrationPoints = this.calibrationPoints.filter(point => point.id !== pointId)
      this.persistCalibrationPoints()
    },
    calibratedVideoTime(event) {
      if(!event || event.timestamp === null || event.timestamp === undefined || !this.calibrationPointsSorted.length) return null

      const points = this.calibrationPointsSorted
      if(points.length === 1) return event.timestamp + this.singleCalibrationOffset

      const previous = [...points].reverse().find(point => point.eventTimestamp <= event.timestamp)
      const next = points.find(point => point.eventTimestamp >= event.timestamp)

      if(previous && next && previous.id !== next.id) {
        const eventRange = next.eventTimestamp - previous.eventTimestamp
        if(eventRange === 0) return previous.videoTime

        const progress = (event.timestamp - previous.eventTimestamp) / eventRange
        return previous.videoTime + progress * (next.videoTime - previous.videoTime)
      }

      const nearest = previous || next
      return event.timestamp + (nearest.videoTime - nearest.eventTimestamp)
    },
    seekToEvent(event) {
      const videoTime = this.calibratedVideoTime(event)
      if(videoTime === null) return

      this.seek(Math.max(0, videoTime))
    },
    loadCalibrationPoints() {
      try {
        const points = JSON.parse(localStorage.getItem(this.calibrationStorageKey)) || []
        this.calibrationPoints = Array.isArray(points) ? points : []
      } catch(e) {
        this.calibrationPoints = []
      }
    },
    persistCalibrationPoints() {
      localStorage.setItem(this.calibrationStorageKey, JSON.stringify(this.calibrationPoints))
    }
  },
  watch: {
    gameId() {
      this.loadCalibrationPoints()
    },
    currentGameUrl(newUrl) {
      if(this.gameUrl || !newUrl) return

      this.useCurrentGame()
    }
  }
}
</script>

<style scoped>
.event-filter {
  max-width: 260px;
}

.event-list {
  max-height: calc(100vh - 300px);
  overflow: auto;
}

.event-card {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.selected-event {
  background: #f5f8ff;
  border-color: var(--v-primary-base) !important;
}

.event-summary {
  line-height: 1.35;
}

.raw-events {
  max-height: calc(100vh - 260px);
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}
</style>
